import { DateTime } from 'luxon';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import { createGuid } from '@/utils/createGuid';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { serializeArray } from '@/utils/jsonPath';
import {
  Automobile,
  AutomobileAccident,
  AutomobileRecall,
  AutomobileSale,
  AutomobileSalvage,
  AutomobileTheft,
  DetailedReportSections,
} from '../types/vehicles';

type DataObj = {
  heading: string;
  value: string | null;
};

export function detailedAccidentsHistory(accidents: AutomobileAccident[]) {
  return accidents && Array.isArray(accidents)
    ? accidents.map((accident) => ({
        parsedDate: getDateFromVehicleEvent(accident),
        location: accidentCrashLocation(accident),
        event: {
          type: {
            name: 'Accident',
            visible: 'Accident Reported',
          },
          data: [
            {
              heading: 'Source',
              value:
                accident && accident.source && accident.source.name
                  ? accident.source.name
                  : null,
            },
          ],
          id: accident.id,
        },
        odometer:
          accident && accident.vehicle ? accident.vehicle.mileage : null,
      }))
    : [];
}

export function detailedRecallsHistory(
  rawRecalls: AutomobileRecall[],
  automobileJsonPath: string | null | undefined
) {
  let parsedRecalls = serializeArray(
    rawRecalls,
    automobileJsonPath || '',
    'recalls'
  );
  parsedRecalls = parsedRecalls || [];
  if (parsedRecalls.length) {
    parsedRecalls = (parsedRecalls as AutomobileRecall[]).map((recall) => {
      const recallStatus = recall?.status?.toLowerCase() || '';
      recall.isOpen = recallStatus === 'open';
      recall.isClosed = recallStatus === 'closed';
      return recall;
    });
    parsedRecalls = (parsedRecalls as AutomobileRecall[]).sort((a, b) => {
      const date1 = a && a.date && a.date.full ? a.date.full : null;
      const date2 = b && b.date && b.date.full ? b.date.full : null;
      if (!date1) {
        return 1;
      }
      if (!date2) {
        return -1;
      }

      return DateTime.fromISO(date2).diff(DateTime.fromISO(date1)).milliseconds;
    });
  }

  type Open = {
    parsedDate?: string | null;
    location: string | null;
    event: {
      type: {
        name: string;
        visible: string;
      };
      data: {
        heading: string;
        value: string | null;
      }[];
      id?: string | null;
    };
  }[];

  const recalls =
    parsedRecalls && Array.isArray(parsedRecalls)
      ? (parsedRecalls as AutomobileRecall[]).reduce((open: Open, recall) => {
          if (recall.isOpen) {
            open.push({
              parsedDate: getDateFromVehicleEvent(recall),
              location: null,
              event: {
                type: {
                  name: 'Recall',
                  visible: 'Recall',
                },
                data: [
                  {
                    heading: 'Components',
                    value:
                      recall && recall.components ? recall.components : null,
                  },
                ],
                id: recall.id,
              },
            });
          }
          return open;
        }, [])
      : [];
  return recalls;
}

export function detailedSalesHistory(sales: AutomobileSale[]) {
  return sales && Array.isArray(sales)
    ? sales.map((sale) => ({
        parsedDate: getDateFromVehicleEvent(sale),
        location: sale && sale.seller && sale.seller.location,
        imageUrl: getFirstImageFromVehicleEvent(sale),
        event: {
          type: {
            name: 'Sale',
            visible: `Listed for sale${
              sale.listing_price
                ? ` for ${commaFormatCurrency(sale.listing_price)}`
                : ''
            }`,
          },
          data: [
            {
              heading: 'Seller',
              value:
                sale && sale.seller && sale.seller.name ? sale.seller.name : '',
            },
          ],
          id: sale.id,
        },
        odometer: sale && sale.vehicle ? sale.vehicle.mileage : null,
      }))
    : [];
}

export function detailedSalvageHistory(salvages: AutomobileSalvage[]) {
  return salvages && Array.isArray(salvages)
    ? salvages.map((salvage) => {
        const data: DataObj[] = [];
        const output = {
          parsedDate: getDateFromVehicleEvent(salvage),
          location: salvage && salvage.location ? salvage.location : null,
          imageUrl: getFirstImageFromVehicleEvent(salvage),
          event: {
            type: {
              name: 'Salvage',
              visible: 'Salvage',
            },
            data,
            id: salvage.id,
          },
          odometer: salvage && salvage.odometer ? salvage.odometer : null,
        };

        if (salvage.premium) {
          output.event.data.push({ heading: 'Source', value: 'NMVTIS' });
        }

        return output;
      })
    : [];
}

export function detailedTheftsHistory(thefts: AutomobileTheft[]) {
  return thefts && Array.isArray(thefts)
    ? thefts.map((theft) => ({
        parsedDate: getDateFromVehicleEvent(theft),
        location:
          theft.source && theft.source.location ? theft.source.location : null,
        event: {
          type: {
            name: 'Theft',
            visible:
              theft && theft.record_type === 'Theft'
                ? 'Theft Reported'
                : (theft && theft.record_type) || '',
          },
          data: [
            {
              heading: 'Source',
              value:
                theft.source && theft.source.name ? theft.source.name : null,
            },
          ],
          id: theft.id,
        },
        odometer: theft && theft.vehicle ? theft.vehicle.mileage : null,
      }))
    : [];
}

function getDateFromVehicleEvent(objectWithDateKey: DetailedReportSections) {
  if (
    objectWithDateKey &&
    objectWithDateKey.date &&
    objectWithDateKey.date.full
  ) {
    return formatDateFromReport(objectWithDateKey.date.full, 'MM/dd/yy');
  }
}

function accidentCrashLocation(accident: AutomobileAccident) {
  if (!accident) return null;
  if (accident.crash_location) return accident.crash_location;
  return `${accident?.source?.contact?.addresses[0]?.parsed?.city}, ${accident?.source?.contact?.addresses[0]?.parsed?.state}`;
}

function getFirstImageFromVehicleEvent(
  objectWithImagesKey: DetailedReportSections
) {
  const hasImages = Boolean(
    objectWithImagesKey &&
      objectWithImagesKey.images &&
      Array.isArray(objectWithImagesKey.images) &&
      objectWithImagesKey.images.length
  );

  if (hasImages) {
    return objectWithImagesKey?.images?.find((image) => image?.url)?.url;
  }
}

type SectionName = 'accidents' | 'recalls' | 'sales' | 'salvage' | 'thefts';

export function addIdsToSections(automobile: Automobile) {
  const sectionsToAddIDsTo = [
    'accidents',
    'recalls',
    'sales',
    'salvage',
    'thefts',
  ];

  sectionsToAddIDsTo.forEach((currentSectionName) => {
    const sectionName: SectionName = currentSectionName as SectionName;
    let section = automobile[sectionName];
    if (section && Array.isArray(section)) {
      section = section.map((element: DetailedReportSections) => {
        return {
          ...element,
          id: createGuid(),
        };
      });
    }

    automobile[sectionName as keyof Automobile] = section;
  });
}
